import React, { useRef, useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Edit2, Trash2, RefreshCw, Loader2, FileCheck } from 'lucide-react';
import { toast } from 'sonner';

const createEmptyEntry = () => ({
  id: null,
  tool_code: '',
  insert_type_code: '',
  operation: '',
  frequency_1st_setup_min: '',
  frequency_1st_setup_max: '',
  frequency_cnc_ind_setup_min: '',
  frequency_cnc_ind_setup_max: '',
  remark: '',
});

const ToolLifeStandardsPage = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'OWNER' || user?.role === 'MANAGER';

  const [standards, setStandards] = useState([]);
  const [parts, setParts] = useState([]);
  const [toolMasters, setToolMasters] = useState([]);
  const [insertTypes, setInsertTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [editingSourceIds, setEditingSourceIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [effectiveOn, setEffectiveOn] = useState('');
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    part_id: '',
    entries: [createEmptyEntry()],
  });

  const fetchStandards = async () => {
    setLoading(true);
    try {
      const params = {};
      if (effectiveOn) params.effective_on = effectiveOn;
      const res = await api.get('/tool-life-standards', { params });
      setStandards(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tool life standards');
      setStandards([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [partsRes, toolsRes, insertsRes] = await Promise.all([
        api.get('/parts'),
        api.get('/tool-master'),
        api.get('/insert-type-master'),
      ]);
      setParts(partsRes.data || []);
      setToolMasters(toolsRes.data || []);
      setInsertTypes(insertsRes.data || []);
    } catch {
      toast.error('Failed to load master data');
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchStandards();
  }, [effectiveOn]);

  const openCreate = () => {
    setDialogMode('create');
    setEditingItem(null);
    setEditingSourceIds([]);
    setForm({
      part_id: '',
      entries: [createEmptyEntry()],
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setDialogMode('edit');
    setEditingItem(row);
    const samePartRows = standards.filter((s) => s.part_id === row.part_id);
    setEditingSourceIds(samePartRows.map((s) => s.id));
    setForm({
      part_id: row.part_id,
      entries: samePartRows.length
        ? samePartRows.map((item) => ({
            id: item.id,
            tool_code: item.tool_code || '',
            insert_type_code: item.insert_type_code || '',
            operation: item.operation || '',
            frequency_1st_setup_min: String(item.frequency_1st_setup_min ?? ''),
            frequency_1st_setup_max: String(item.frequency_1st_setup_max ?? ''),
            frequency_cnc_ind_setup_min: String(item.frequency_cnc_ind_setup_min ?? ''),
            frequency_cnc_ind_setup_max: String(item.frequency_cnc_ind_setup_max ?? ''),
            remark: item.remark || '',
          }))
        : [createEmptyEntry()],
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!dialogOpen) return;
  }, [dialogOpen, dialogMode, canEdit]);

  const handleSave = async () => {
    if (!form.part_id?.trim()) {
      toast.error('Part is required');
      return;
    }
    if (!Array.isArray(form.entries) || form.entries.length === 0) {
      toast.error('At least one tool/insert row is required');
      return;
    }

    const normalizedEntries = [];
    for (let index = 0; index < form.entries.length; index += 1) {
      const entry = form.entries[index];
      const min1 = parseInt(entry.frequency_1st_setup_min, 10);
      const max1 = parseInt(entry.frequency_1st_setup_max, 10);
      const minCnc = parseInt(entry.frequency_cnc_ind_setup_min, 10);
      const maxCnc = parseInt(entry.frequency_cnc_ind_setup_max, 10);
      if (!entry.tool_code?.trim()) {
        toast.error(`Row ${index + 1}: Tool type is required`);
        return;
      }
      if (!entry.insert_type_code?.trim()) {
        toast.error(`Row ${index + 1}: Insert type is required`);
        return;
      }
      if (isNaN(min1) || min1 < 0 || isNaN(max1) || max1 < 0) {
        toast.error(`Row ${index + 1}: 1st set up min/max must be non-negative numbers`);
        return;
      }
      if (isNaN(minCnc) || minCnc < 0 || isNaN(maxCnc) || maxCnc < 0) {
        toast.error(`Row ${index + 1}: CNC Ind set up min/max must be non-negative numbers`);
        return;
      }
      normalizedEntries.push({
        ...entry,
        tool_code: entry.tool_code.trim(),
        insert_type_code: entry.insert_type_code.trim(),
        operation: entry.operation.trim(),
        remark: entry.remark.trim(),
        frequency_1st_setup_min: min1,
        frequency_1st_setup_max: max1,
        frequency_cnc_ind_setup_min: minCnc,
        frequency_cnc_ind_setup_max: maxCnc,
      });
    }

    setSaving(true);
    try {
      if (dialogMode === 'create') {
        await Promise.all(
          normalizedEntries.map((entry) =>
            api.post('/tool-life-standards', {
              part_id: form.part_id,
              tool_code: entry.tool_code,
              insert_type_code: entry.insert_type_code,
              operation: entry.operation,
              frequency_1st_setup_min: entry.frequency_1st_setup_min,
              frequency_1st_setup_max: entry.frequency_1st_setup_max,
              frequency_cnc_ind_setup_min: entry.frequency_cnc_ind_setup_min,
              frequency_cnc_ind_setup_max: entry.frequency_cnc_ind_setup_max,
              remark: entry.remark,
            })
          )
        );
        toast.success('Tool life standard created');
      } else {
        const editedIds = normalizedEntries.map((entry) => entry.id).filter(Boolean);
        const deletedIds = editingSourceIds.filter((id) => !editedIds.includes(id));

        await Promise.all(
          normalizedEntries.map((entry) => {
            const payload = {
              part_id: form.part_id,
              tool_code: entry.tool_code,
              insert_type_code: entry.insert_type_code,
              operation: entry.operation,
              frequency_1st_setup_min: entry.frequency_1st_setup_min,
              frequency_1st_setup_max: entry.frequency_1st_setup_max,
              frequency_cnc_ind_setup_min: entry.frequency_cnc_ind_setup_min,
              frequency_cnc_ind_setup_max: entry.frequency_cnc_ind_setup_max,
              remark: entry.remark,
            };
            if (entry.id) {
              return api.put(`/tool-life-standards/${entry.id}`, payload);
            }
            return api.post('/tool-life-standards', payload);
          })
        );
        await Promise.all(deletedIds.map((id) => api.delete(`/tool-life-standards/${id}`)));
        toast.success('Tool life standard updated');
      }
      setDialogOpen(false);
      fetchStandards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tool life standard?')) return;
    try {
      await api.delete(`/tool-life-standards/${id}`);
      toast.success('Tool life standard deleted');
      fetchStandards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDeletePartGroup = async (rows) => {
    if (!rows?.length) return;
    const partLabel = rows[0]?.part_no || rows[0]?.part_id || 'this part';
    if (!window.confirm(`Delete all tool life entries for ${partLabel}?`)) return;
    try {
      await Promise.all(rows.map((row) => api.delete(`/tool-life-standards/${row.id}`)));
      toast.success(`Deleted ${rows.length} entries for ${partLabel}`);
      fetchStandards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete part entries');
    }
  };

  const triggerImportPicker = () => {
    if (importing) return;
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const body = new FormData();
    body.append('file', file);

    setImporting(true);
    try {
      const res = await api.post('/tool-life-standards/import', body, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const summary = res.data || {};
      toast.success(
        `Import completed. Processed ${summary.processed ?? 0}, inserted ${summary.inserted ?? 0}, updated ${summary.updated ?? 0}, failed ${summary.failed ?? 0}.`
      );
      fetchStandards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const groupedStandards = standards.reduce((acc, row) => {
    const key = row.part_no || row.part_id || 'UNKNOWN';
    if (!acc[key]) {
      acc[key] = {
        partLabel: key,
        rows: [],
      };
    }
    acc[key].rows.push(row);
    return acc;
  }, {});

  const groupedRows = Object.values(groupedStandards);

  const updateEntryField = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      entries: prev.entries.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
    }));
  };

  const addEntryRow = () => {
    setForm((prev) => ({ ...prev, entries: [...prev.entries, createEmptyEntry()] }));
  };

  const removeEntryRow = (index) => {
    setForm((prev) => {
      if (prev.entries.length <= 1) return prev;
      return { ...prev, entries: prev.entries.filter((_, i) => i !== index) };
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tool-life-standards-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Tool Life Standards</h1>
          <p className="text-slate-500">Audit benchmark table (SU/PRD/F-05): part, tool type, insert type, insert change frequency</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-slate-600 whitespace-nowrap">Effective on:</Label>
            <Input
              type="date"
              value={effectiveOn}
              onChange={(e) => setEffectiveOn(e.target.value)}
              className="w-40"
            />
          </div>
          <Button variant="outline" onClick={fetchStandards} data-testid="refresh-standards-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canEdit && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleImportFile}
                data-testid="tool-life-import-input"
              />
              <Button
                variant="outline"
                onClick={triggerImportPicker}
                disabled={importing}
                data-testid="import-standards-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Import File
              </Button>
            </>
          )}
          {canEdit && (
            <Button onClick={openCreate} data-testid="add-standard-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Standard
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            Benchmark entries
          </CardTitle>
          <CardDescription>
            {effectiveOn ? `Standards effective on ${effectiveOn}` : 'Current standards (no date filter)'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Part No</TableHead>
                <TableHead>Combined Tool / Insert / Intervals</TableHead>
                <TableHead>Effective</TableHead>
                {canEdit && <TableHead className="w-40">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 4 : 3} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : groupedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 4 : 3} className="text-center py-8 text-slate-500">
                    No tool life standards found. {canEdit ? 'Add a standard to get started.' : ''}
                  </TableCell>
                </TableRow>
              ) : (
                groupedRows.map((group) => (
                  <TableRow key={group.partLabel}>
                    <TableCell className="font-mono text-sm align-top">{group.partLabel}</TableCell>
                    <TableCell className="align-top">
                      <div className="space-y-2">
                        {group.rows.map((row) => (
                          <div key={row.id} className="text-sm border border-slate-200 rounded-md p-2">
                            <div className="font-mono">
                              {row.tool_code} / {row.insert_type_code}
                            </div>
                            <div className="text-slate-700">
                              1st: {row.frequency_1st_setup_min}–{row.frequency_1st_setup_max} PCS | CNC: {row.frequency_cnc_ind_setup_min}–{row.frequency_cnc_ind_setup_max} PCS
                            </div>
                            <div className="text-slate-500 truncate" title={row.operation || ''}>
                              {row.operation || '—'}
                            </div>
                            {row.remark ? (
                              <div className="text-slate-500 truncate" title={row.remark}>
                                Note: {row.remark}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 align-top">
                      <div className="space-y-2">
                        {group.rows.map((row) => (
                          <div key={`eff-${row.id}`}>
                            {row.effective_from}
                            {row.effective_to ? ` → ${row.effective_to}` : ' (current)'}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="align-top">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(group.rows[0])}
                            className="h-8 w-8"
                            title="Edit all entries for this part"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeletePartGroup(group.rows)}
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            title="Delete all entries for this part"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'create' ? 'Add Tool Life Standard' : 'Edit Tool Life Standard'}</DialogTitle>
            <DialogDescription>Part, tool type, insert type, operation and insert change frequency (PCS).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Part *</Label>
              <Select
                value={form.part_id}
                onValueChange={(v) => setForm((f) => ({ ...f, part_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select part" />
                </SelectTrigger>
                <SelectContent>
                  {parts.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.part_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Tool + Insert Rows</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEntryRow}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </Button>
              </div>
              {form.entries.map((entry, idx) => (
                <div key={entry.id || `new-${idx}`} className="border rounded-md p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tool type *</Label>
                      <Select value={entry.tool_code} onValueChange={(v) => updateEntryField(idx, 'tool_code', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select tool" />
                        </SelectTrigger>
                        <SelectContent>
                          {toolMasters.map((t) => (
                            <SelectItem key={t.id} value={t.tool_code}>{t.tool_code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Insert type *</Label>
                      <Select value={entry.insert_type_code} onValueChange={(v) => updateEntryField(idx, 'insert_type_code', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select insert type" />
                        </SelectTrigger>
                        <SelectContent>
                          {insertTypes.map((i) => (
                            <SelectItem key={i.id} value={i.insert_type_code}>{i.insert_type_code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Operation</Label>
                    <Input
                      value={entry.operation}
                      onChange={(e) => updateEntryField(idx, 'operation', e.target.value)}
                      placeholder="e.g. ONE SIDE FACE, WIDTH, BORE"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>1st set up min (PCS) *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={entry.frequency_1st_setup_min}
                        onChange={(e) => updateEntryField(idx, 'frequency_1st_setup_min', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>1st set up max (PCS) *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={entry.frequency_1st_setup_max}
                        onChange={(e) => updateEntryField(idx, 'frequency_1st_setup_max', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>CNC Ind set up min (PCS) *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={entry.frequency_cnc_ind_setup_min}
                        onChange={(e) => updateEntryField(idx, 'frequency_cnc_ind_setup_min', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CNC Ind set up max (PCS) *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={entry.frequency_cnc_ind_setup_max}
                        onChange={(e) => updateEntryField(idx, 'frequency_cnc_ind_setup_max', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Remark</Label>
                    <Textarea
                      value={entry.remark}
                      onChange={(e) => updateEntryField(idx, 'remark', e.target.value)}
                      placeholder="Optional notes"
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-red-600 bg-red-600 text-white hover:bg-red-700 hover:border-red-700 hover:text-white"
                      onClick={() => removeEntryRow(idx)}
                      disabled={form.entries.length <= 1}
                    >
                      Remove Row
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {canEdit && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {dialogMode === 'create' ? 'Create' : 'Save'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ToolLifeStandardsPage;
